import React, { createContext, memo, useContext, useState, type FC, type ReactNode } from 'react';
import { TouchableOpacity, View, type ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

export type CollapsibleProps = {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  animated?: boolean;
  containerStyle?: ViewStyle | ViewStyle[];
};

export type CollapsibleTriggerProps = {
  children: ReactNode | ((isOpen: boolean) => ReactNode);
  hideDefaultIcon?: boolean;
  triggerStyle?: ViewStyle | ViewStyle[];
};

export type CollapsibleContentProps = {
  children: ReactNode;
  contentStyle?: ViewStyle | ViewStyle[];
  outerStyle?: ViewStyle | ViewStyle[];
};

type CollapsibleContextValue = {
  disabled: boolean;
  isOpen: boolean;
  animated: boolean;
  toggle: () => void;
};

const CollapsibleContext = createContext<CollapsibleContextValue | null>(null);

function useCollapsibleContext() {
  const context = useContext(CollapsibleContext);
  if (!context) {
    throw new Error('Collapsible.Trigger and Collapsible.Content must be used within Collapsible.');
  }

  return context;
}

const CollapsibleRoot: FC<CollapsibleProps> = memo(
  ({
    open,
    defaultOpen = false,
    onOpenChange,
    disabled = false,
    animated = true,
    containerStyle,
    children,
  }) => {
    const [internalOpen, setInternalOpen] = useState<boolean>(defaultOpen);

    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : internalOpen;

    const toggle = () => {
      if (disabled) {
        return;
      }
      const next = !isOpen;
      if (!isControlled) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
    };

    return (
      <CollapsibleContext.Provider
        value={{
          isOpen,
          toggle,
          disabled,
          animated,
        }}>
        <Stack direction="column" overflow="hidden" style={containerStyle}>
          {children}
        </Stack>
      </CollapsibleContext.Provider>
    );
  },
);

const CollapsibleTrigger: FC<CollapsibleTriggerProps> = memo(
  ({ children, hideDefaultIcon = false, triggerStyle }) => {
    const { isOpen, toggle, disabled } = useCollapsibleContext();
    const { scale } = useScaling();
    const styles = useStyles();

    return (
      <TouchableOpacity
        onPress={toggle}
        disabled={disabled}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen, disabled }}>
        <Stack direction="row" align="center" justify="space-between" style={triggerStyle}>
          <Stack flex={1}>
            {typeof children === 'string' ? (
              <Text>{children}</Text>
            ) : typeof children === 'function' ? (
              children(isOpen)
            ) : (
              children
            )}
          </Stack>
          {!hideDefaultIcon ? (
            <View style={styles.icon}>
              <SvgIcon
                name="IcBack"
                transform={[{ rotate: isOpen ? '90deg' : '-90deg' }]}
                size={scale(16)}
                color={'#E97D27'}
              />
            </View>
          ) : null}
        </Stack>
      </TouchableOpacity>
    );
  },
);

const CollapsibleContent: FC<CollapsibleContentProps> = memo(
  ({ children, contentStyle, outerStyle }) => {
    const { isOpen, animated } = useCollapsibleContext();
    const styles = useStyles();

    if (!animated) {
      if (!isOpen) return null;
      return <Stack style={contentStyle}>{children}</Stack>;
    }

    return (
      <CollapsibleAnimatedContent
        isOpen={isOpen}
        contentStyle={contentStyle}
        styles={styles}
        outerStyle={outerStyle}>
        {children}
      </CollapsibleAnimatedContent>
    );
  },
);

type CollapsibleAnimatedContentProps = CollapsibleContentProps & {
  isOpen: boolean;
  styles: ReturnType<typeof useStyles>;
  outerStyle?: ViewStyle | ViewStyle[];
};

const CollapsibleAnimatedContent: FC<CollapsibleAnimatedContentProps> = memo(
  ({ children, contentStyle, isOpen, styles, outerStyle }) => {
    const measuredHeight = useSharedValue(0);
    const openProgress = useDerivedValue(
      () => withTiming(Number(isOpen), { duration: 300 }),
      [isOpen],
    );
    const bodyStyle = useAnimatedStyle(() => ({
      height: measuredHeight.value * openProgress.value,
    }));

    return (
      <Animated.View style={[styles.contentWrapper, bodyStyle, outerStyle]}>
        <View
          onLayout={e => {
            measuredHeight.value = e.nativeEvent.layout.height;
          }}
          style={styles.contentInner}>
          <Stack style={contentStyle}>{children}</Stack>
        </View>
      </Animated.View>
    );
  },
);

type CollapsibleCompoundComponent = FC<CollapsibleProps> & {
  Trigger: FC<CollapsibleTriggerProps>;
  Content: FC<CollapsibleContentProps>;
};

export const Collapsible = Object.assign(CollapsibleRoot, {
  Trigger: CollapsibleTrigger,
  Content: CollapsibleContent,
}) as CollapsibleCompoundComponent;

const useStyles = createStyles(({ scale }) => ({
  icon: {
    marginLeft: scale(8),
  },
  contentWrapper: {
    overflow: 'hidden',
  },
  contentInner: {
    width: '100%',
    position: 'absolute',
  },
}));
